import { useEffect, useState } from 'react';

import {
  Client as ConversationsClient,
  Conversation,
  Message,
  Paginator,
} from '@twilio/conversations';
import {
  findConversationByPatientId,
  getIdentityFromUser,
} from 'utils/twilio/twilio';

import { useLogger } from 'hooks/useLogger/useLogger';
import { getChat } from 'services/chat';

import { ChatEvents } from './types';

const useConversations = (
  setMessages: (
    messages:
      | Partial<Message>[]
      | ((prevMessages: Partial<Message>[]) => Partial<Message>[]),
  ) => void,
  setHydratedMessages: (messages: Partial<Message>[]) => void,
  patientId?: string,
  onMessageAdded?: () => void,
) => {
  const logger = useLogger();

  const [identity, setIdentity] = useState('');
  const [isChatActive, setIsChatActive] = useState(false);
  const [isActivePatientTyping, setIsActivePatientTyping] = useState(false);
  const [patientsTyping, setPatientsTyping] = useState<string[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [client, setClient] = useState<ConversationsClient | undefined>();
  const [conversationAllowed, setConversationAllowed] = useState<string[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation>();

  const getAccessToken = async (refresh = false) => {
    try {
      const { data } = await getChat();

      setIdentity(getIdentityFromUser(data.access_token));
      setClient(new ConversationsClient(data.access_token));

      setConversationAllowed(data?.conversation_ids);

      if (refresh) {
        return data?.access_token;
      }

      return null;
    } catch (e) {
      // eslint-disable-next-line no-console
      logger.error('getAccessToken', { e });
    }

    return null;
  };

  const init = async () => {
    try {
      await getAccessToken();
    } catch (e) {
      // eslint-disable-next-line no-console
      logger.error('init', { e });
    }
  };

  async function loadMessages(
    conversation: Conversation,
  ): Promise<Paginator<Message>> {
    try {
      return await conversation.getMessages();
    } catch {
      throw new Error('Error while loading messages');
    }
  }

  function hydrateMessages(messages: Partial<Message>[]) {
    const items: Partial<Message>[][] = [];
    let aux: Partial<Message>[] = [];

    const hydrateLastMessage = (index: number) =>
      index === messages.length - 1 && items.push(aux);

    messages.forEach((message, index) => {
      if (index === 0) {
        aux.push(message);
        hydrateLastMessage(index);
        return;
      }

      if (message.participantSid === messages[index - 1].participantSid) {
        aux.push(message);
        hydrateLastMessage(index);
        return;
      }

      items.push(aux);
      aux = [message];

      hydrateLastMessage(index);
    });

    return items.flat();
  }

  const handleMessageAdded = (
    message: Partial<Message>,
    currentConversation?: Conversation,
  ) => {
    if (
      currentConversation &&
      message?.conversation?.sid === currentConversation.sid
    ) {
      setMessages((prevMessages: Partial<Message>[]) => {
        if (prevMessages.length === 0) {
          const msg = hydrateMessages([message]);
          setHydratedMessages(msg);
          return [message];
        }

        const messageChunks = hydrateMessages([...prevMessages, message]);
        setHydratedMessages(messageChunks);

        return [...prevMessages, message];
      });
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (client && conversationAllowed.length) {
      client.on(ChatEvents.CONNECTION_STATE_CHANGE, state => {
        if (state === ChatEvents.CONNECTED) {
          setIsChatActive(true);
          // eslint-disable-next-line no-console
          logger.info('Connected to Twilio…');
        }

        if (state === ChatEvents.DISCONNECTED) {
          setIsChatActive(false);
          // eslint-disable-next-line no-console
          logger.info('Twilio disconnected…');
        }
        if (state === ChatEvents.DENIED) {
          setIsChatActive(false);
          // eslint-disable-next-line no-console
          logger.info('Twilio denied…');
        }
      });

      client.on(ChatEvents.TOKEN_EXPIRE, async () => {
        const newToken = await getAccessToken(true);
        if (newToken) {
          client.updateToken(newToken);
        }
      });

      client.on(
        ChatEvents.CONVERSATION_JOINED,
        (conversation: Conversation) => {
          try {
            if (conversationAllowed?.includes(conversation.sid)) {
              setConversations(prevConversations => {
                if (prevConversations.length === 0) {
                  return [conversation];
                }

                const conversationsChunks = [
                  ...prevConversations,
                  conversation,
                ];
                setConversations(conversationsChunks);

                return conversationsChunks;
              });
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            logger.error('e', { e });
          }
        },
      );
      client.on(ChatEvents.CONVERSATION_LEFT, (conversation: Conversation) => {
        setConversations(
          conversations.filter(cv => conversation.sid !== cv.sid),
        );
      });
      client.on(ChatEvents.MESSAGE_ADDED, (message: Partial<Message>) => {
        handleMessageAdded(message, selectedConversation);

        if (onMessageAdded) {
          onMessageAdded();
        }
      });
    }

    return () => {
      client?.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, conversationAllowed, selectedConversation]);

  const handleConversationSelection = async (
    selectedPatientId: string,
    conversationsArray: Conversation[],
  ) => {
    const conversationSelected = await findConversationByPatientId(
      selectedPatientId,
      conversationsArray,
    );

    setSelectedConversation(conversationSelected);
  };

  useEffect(() => {
    if (patientId && conversations.length) {
      handleConversationSelection(patientId, conversations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      selectedConversation.on(
        ChatEvents.TYPING_STARTED,
        ({ identity: patientIdentity }) => {
          if (patientIdentity) {
            setPatientsTyping([...patientsTyping, patientIdentity]);
          }
        },
      );
      selectedConversation.on(
        ChatEvents.TYPING_ENDED,
        ({ identity: patientIdentity }) => {
          setPatientsTyping(
            patientsTyping.filter(
              typingPatientId => typingPatientId !== patientIdentity,
            ),
          );
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  useEffect(() => {
    if (patientId) {
      if (patientsTyping.includes(patientId)) {
        setIsActivePatientTyping(true);
      } else {
        setIsActivePatientTyping(false);
      }
    } else {
      setIsActivePatientTyping(false);
    }
  }, [patientsTyping, patientId]);

  return {
    identity,
    patientId,
    isChatActive,
    loadMessages,
    hydrateMessages,
    selectedConversation,
    isPatientTyping: isActivePatientTyping,
  };
};

export { useConversations };
